// AdminDashboard Component - Dashboard overview with stats and quick links
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Users, Wallet, History, TrendingUp, ChevronRight, ShoppingCart, Plug, Download, HelpCircle } from 'lucide-react';
import { formatCAD } from '../../utils/currency';
import { useAdmin } from './AdminContext';

export default function AdminDashboard({ setActiveTab, onDownloadPlugin, onShowGuide, stats: propStats }) {
  const adminContext = useAdmin();
  // Use props stats if provided, otherwise fallback to context
  const stats = propStats || adminContext?.stats;
  const t = adminContext?.t || ((key) => key);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="overflow-hidden">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats.total_users}</p>
                  <p className="text-sm text-muted-foreground">{t('admin.users')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="overflow-hidden">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
                  <Wallet className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats.total_wallets}</p>
                  <p className="text-sm text-muted-foreground">Wallets</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="overflow-hidden">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <History className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats.total_transactions}</p>
                  <p className="text-sm text-muted-foreground">{t('admin.transactions')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="overflow-hidden">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{formatCAD(stats.total_balance)}</p>
                  <p className="text-sm text-muted-foreground">{t('admin.total_balance')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Links */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-blue-500" 
          onClick={() => setActiveTab('users-list')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{t('admin.all_users')}</p>
                  <p className="text-sm text-muted-foreground">{t('admin.manage_accounts')}</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-purple-500" 
          onClick={() => setActiveTab('transactions')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <History className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium">{t('admin.transactions')}</p>
                  <p className="text-sm text-muted-foreground">{t('admin.total_transactions')}</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-amber-500" 
          onClick={() => setActiveTab('woo-apikeys')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <ShoppingCart className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium">WooCommerce & IPN</p>
                  <p className="text-sm text-muted-foreground">Clés API & Notifications</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* WooCommerce Plugin Download */}
      <Card className="border-2 border-dashed border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <Plug className="h-7 w-7 text-white" />
              </div>
              <div>
                <p className="font-semibold text-lg">{t('admin.woocommerce_plugin')}</p>
                <p className="text-sm text-muted-foreground">{t('admin.woocommerce_plugin_desc')}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onShowGuide} className="gap-2" data-testid="open-guide-btn">
                <HelpCircle className="h-4 w-4" />
                {t('admin.guide')}
              </Button>
              <Button 
                onClick={onDownloadPlugin} 
                className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700" 
                data-testid="download-woocommerce-plugin-btn"
              >
                <Download className="h-4 w-4" />
                {t('admin.download')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
